import {ThemeIconKey} from 'platform/foundation';

import type {ActionDefinition, ActionDefinitionObject} from '../types/ActionDefinition';
import type {RowActions} from '../types/RowData';
import {collect} from './partitionRowActions';

export type ContextMenuActionItem = {
  actionKey: string;
  title: string;
  icon: ThemeIconKey | undefined;
  isHighlighted: boolean;
  isNewTab?: boolean;
};

export type ContextMenuSection = {
  group: string | null;
  items: ContextMenuActionItem[];
};

const NEW_TAB_ACTION_SUFFIX = '_newTab';
const NEW_TAB_ACTION_KEYS_WITHOUT_LINK = new Set(['detail', 'redirectDetail']);

function getContextMenuItems(
  actionKey: string,
  definition: ActionDefinition,
  rowClickKey: string | null,
  explicitActionKeys: Set<string>
): ContextMenuActionItem[] {
  const item: ContextMenuActionItem = {
    actionKey,
    title: definition.title,
    icon: definition.icon.value,
    isHighlighted: actionKey === rowClickKey,
  };
  const newTabActionKey = `${actionKey}${NEW_TAB_ACTION_SUFFIX}`;

  if (
    (!definition.isLink && !NEW_TAB_ACTION_KEYS_WITHOUT_LINK.has(actionKey)) ||
    actionKey.endsWith(NEW_TAB_ACTION_SUFFIX) ||
    explicitActionKeys.has(newTabActionKey)
  ) {
    return [item];
  }

  return [item, {...item, actionKey: newTabActionKey, isHighlighted: false, isNewTab: true}];
}

/**
 * Builds the grouped model for the row context menu from the API's row-action buckets.
 *
 * Order of consideration: rowClick first, then primary, then secondary (deduped by key).
 * Bulk-only actions (singleAction === false) and keys with no definition are excluded.
 * Ungrouped actions form a headerless top section (group: null) with the rowClick action
 * highlighted; grouped actions follow, one section per distinct `group` in first-seen order.
 */
export function getRowContextMenuSections(
  rowActions: RowActions,
  actionsDefinition: ActionDefinitionObject
): ContextMenuSection[] {
  const rowClickKey = rowActions.rowClick ?? null;
  const orderedKeys = [
    ...(rowClickKey ? [rowClickKey] : []),
    ...rowActions.primary,
    ...rowActions.secondary,
  ];

  const seen = new Set<string>();
  const eligibleActions = collect(orderedKeys, actionsDefinition);
  const explicitActionKeys = new Set(eligibleActions.map(({actionKey}) => actionKey));
  const ungrouped: ContextMenuActionItem[] = [];
  const grouped = new Map<string, ContextMenuActionItem[]>();

  // collect() applies the shared eligibility filter (definition exists && singleAction); we
  // still dedupe here because a key can appear in more than one of the ordered buckets.
  for (const {actionKey, definition} of eligibleActions) {
    if (seen.has(actionKey)) {
      continue;
    }
    seen.add(actionKey);

    const items = getContextMenuItems(actionKey, definition, rowClickKey, explicitActionKeys);

    const group = definition.group;
    if (group === undefined || group === null || group === '') {
      ungrouped.push(...items);
    } else {
      const bucket = grouped.get(group);
      if (bucket) {
        bucket.push(...items);
      } else {
        grouped.set(group, items);
      }
    }
  }

  const sections: ContextMenuSection[] = [];
  if (ungrouped.length > 0) {
    sections.push({group: null, items: ungrouped});
  }
  for (const [group, items] of grouped) {
    sections.push({group, items});
  }
  return sections;
}
